// components/StripeCardElement.jsx
import React, { useState, useEffect, useRef } from 'react';
import { 
  CardNumberElement, 
  CardExpiryElement, 
  CardCvcElement 
} from '@stripe/react-stripe-js';
import Icon from '../../../components/AppIcon';

const StripeCardElement = ({ onPaymentMethodReady, disabled }) => {
  const [errors, setErrors] = useState({});
  const [completeness, setCompleteness] = useState({
    cardNumber: false,
    cardExpiry: false,
    cardCvc: false,
  });
  const onReadyRef = useRef(onPaymentMethodReady);

  useEffect(() => {
    onReadyRef.current = onPaymentMethodReady;
  }, [onPaymentMethodReady]);

  useEffect(() => {
    const allComplete = Object.values(completeness).every((complete) => complete);
    onReadyRef.current?.(allComplete);
  }, [completeness]);

  const handleChange = (field) => (event) => {
    if (event.error) {
      setErrors((prev) => ({ ...prev, [field]: event.error.message }));
    } else {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }

    setCompleteness((prev) => ({ ...prev, [field]: event.complete }));
  };

  const elementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: 'var(--color-foreground)',
        fontFamily: 'Inter, system-ui, sans-serif',
        '::placeholder': {
          color: 'var(--color-muted-foreground)',
        },
        iconColor: 'var(--color-primary)',
      },
      invalid: {
        color: 'var(--color-error)',
        iconColor: 'var(--color-error)',
      },
    },
  };

  const getFieldClassName = (field) => {
    const hasError = errors[field];
    const isComplete = completeness[field];
    
    return `p-4 border-2 rounded-lg transition-all duration-200 ${
      hasError
        ? 'border-error bg-error/5'
        : isComplete
        ? 'border-success bg-success/5'
        : 'border-border bg-background'
    }`;
  };

  return (
    <div className="w-full space-y-4">
      {/* Numéro de carte */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Numéro de carte
        </label>
        <div className={getFieldClassName('cardNumber')}>
          <CardNumberElement
            options={elementOptions}
            onChange={handleChange('cardNumber')}
            disabled={disabled}
          />
        </div>
        {errors.cardNumber && (
          <div className="flex items-start gap-2 mt-2">
            <Icon name="AlertCircle" size={16} className="text-error mt-0.5" />
            <p className="text-sm text-error">{errors.cardNumber}</p>
          </div>
        )}
      </div>

      {/* Date d'expiration et CVC */}
      <div className="grid grid-cols-2 gap-4">
        {/* Date d'expiration */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Date d'expiration
          </label>
          <div className={getFieldClassName('cardExpiry')}>
            <CardExpiryElement
              options={elementOptions}
              onChange={handleChange('cardExpiry')}
              disabled={disabled}
            />
          </div>
          {errors.cardExpiry && (
            <div className="flex items-start gap-2 mt-2">
              <Icon name="AlertCircle" size={16} className="text-error mt-0.5" />
              <p className="text-sm text-error">{errors.cardExpiry}</p>
            </div>
          )}
        </div>

        {/* CVC */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            CVC
          </label>
          <div className={getFieldClassName('cardCvc')}>
            <CardCvcElement
              options={elementOptions}
              onChange={handleChange('cardCvc')}
              disabled={disabled}
            />
          </div>
          {errors.cardCvc && (
            <div className="flex items-start gap-2 mt-2">
              <Icon name="AlertCircle" size={16} className="text-error mt-0.5" />
              <p className="text-sm text-error">{errors.cardCvc}</p>
            </div>
          )}
        </div>
      </div>

      {/* Message de sécurité */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Icon name="Lock" size={14} />
        <span>Paiement sécurisé par Stripe</span>
      </div>
    </div>
  );
};

export default StripeCardElement;